import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebaseConfig";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Visible checkbox widget state
  const widgetRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const siteKey = process.env.REACT_APP_CONTACT_RECAPTCHA_SITE_KEY;

  // Render a **visible** checkbox once enterprise API is ready
  useEffect(() => {
    let mounted = true;

    const tryRender = () => {
      if (!mounted || !window.grecaptcha?.enterprise) return;

      const el = document.getElementById("contact-recaptcha");
      if (!el) return;

      // If already rendered, don't render twice
      if (
        widgetRef.current !== null &&
        typeof widgetRef.current !== "undefined"
      )
        return;

      widgetRef.current = window.grecaptcha.enterprise.render(el, {
        sitekey: siteKey,
        size: "normal", // <-- visible checkbox
        theme: "light",
        callback: (token) => setCaptchaToken(token), // solved
        "expired-callback": () => setCaptchaToken(""), // expired
        "error-callback": () => setCaptchaToken(""), // failed
      });
    };

    // Wait until the script is present and ready, then render
    const i = setInterval(() => {
      if (window.grecaptcha?.enterprise) {
        clearInterval(i);
        window.grecaptcha.enterprise.ready(tryRender);
      }
    }, 200);

    return () => {
      mounted = false;
      clearInterval(i);
    };
  }, [siteKey]);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      if (!captchaToken) {
        throw new Error(t("contact.captchaError"));
      }

      // Call your callable with the user-entered data + captcha token
      const submitContactMessage = httpsCallable(
        functions,
        "submitContactMessage"
      );
      const { data } = await submitContactMessage({
        ...form,
        recaptchaToken: captchaToken,
      });

      if (data?.ok) {
        setResult({ ok: true });
        setForm({ name: "", email: "", company: "", message: "" });

        // Reset the checkbox so they can submit again if needed
        if (widgetRef.current != null && window.grecaptcha?.enterprise) {
          window.grecaptcha.enterprise.reset(widgetRef.current);
        }
        setCaptchaToken("");
      } else {
        throw new Error(data?.message || "Submission failed.");
      }
    } catch (err) {
      setResult({ ok: false, message: err.message || "Unexpected error." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h4" gutterBottom>
        {t("contact.title")}
      </Typography>
      <Typography variant="body1" gutterBottom>
        {t("contact.subtitle")}
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <TextField
          label={t("contact.name")}
          name="name"
          value={form.name}
          onChange={onChange}
          required
          fullWidth
        />
        <TextField
          label={t("contact.email")}
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          required
          fullWidth
        />
        <TextField
          label={t("contact.company")}
          name="company"
          value={form.company}
          onChange={onChange}
          fullWidth
        />
        <TextField
          label={t("contact.message")}
          name="message"
          value={form.message}
          onChange={onChange}
          required
          multiline
          rows={4}
          fullWidth
        />

        {/* Visible reCAPTCHA checkbox mounts here */}
        <div id="contact-recaptcha" style={{ marginTop: 8, marginBottom: 8 }} />

        <Button type="submit" variant="contained" disabled={submitting}>
          {submitting ? t("contact.sending") : t("contact.send")}
        </Button>
      </Box>

      {result && (
        <Alert severity={result.ok ? "success" : "error"} sx={{ mt: 2 }}>
          {result.ok
            ? t("contact.success")
            : result.message || t("contact.error")}
        </Alert>
      )}
    </Container>
  );
}
